<script setup lang="ts">
import { computed, type PropType, onMounted, ref } from 'vue'
import Order from '@/components/checkout/Order.vue'
import FundingSourceSelector from '@/components/checkout/FundingSourceSelector.vue'
import { useViewportSize } from '@/components/composables/useViewportSize'
import { OrderItemType } from '@/types/orderItemType'
import { formatCurrency } from '@/classes/Helpers.ts'
import eventBus from '@/classes/EventBus'

const emit = defineEmits([
  'closeCheckoutModal',
  'checkout',
  'select-funding-source',
  'update:modelValue'
])

const { isMdOrGreater } = useViewportSize()

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  district: {
    type: Object as PropType<TDistrict>,
    required: true
  },
  orderEstimate: {
    type: Array as PropType<TOrderItem[][] | null>,
    required: true
  },
  fundingSources: {
    type: Array as PropType<TFundingSource[] | null>,
    required: true
  },
  selectedFundingSourceId: {
    type: [Number, null] as PropType<number | null>,
    required: true
  },
  checkoutInProgress: {
    type: Boolean,
    required: true
  },
  loadingOrderEstimate: {
    type: Boolean,
    required: true
  },
  autoPayWarning: {
    type: Boolean,
    default: false
  }
})

const vericheckTermsAccepted = ref(false)
const feeConfirmation = ref(false)

/**
 * Whether an estimate is already on screen. A refresh keeps it there instead
 * of swapping in the spinner, because the swap shrinks the dialog, re-centres
 * it, and moves every control in it while the family is about to click.
 * @returns {boolean}
 */
const hasOrderEstimate = computed(
  () => props.orderEstimate !== null && props.orderEstimate.length > 0
)

const payIsDisabled = computed(() => {
  // The estimate on screen is stale until the refresh lands — picking a card
  // changes its processing fee — so the family never pays against it.
  if (props.loadingOrderEstimate) return true

  // Calculate the total amount
  let totalAmount = 0
  if (props.orderEstimate !== null) {
    props.orderEstimate.forEach(order => {
      order.forEach(item => {
        totalAmount += item.amount
      })
    })
  }

  // For zero-dollar checkouts, don't require funding sources
  if (totalAmount === 0) {
    return props.checkoutInProgress || (requiresFeeCheckbox.value && !feeConfirmation.value)
  }

  // For non-zero checkouts, require all normal validations
  return (
    !props.fundingSources ||
    props.fundingSources.length === 0 ||
    !props.selectedFundingSourceId ||
    props.checkoutInProgress ||
    (fundingSourceIsVericheck.value && !vericheckTermsAccepted.value) ||
    (requiresFeeCheckbox.value && !feeConfirmation.value)
  )
})

/**
 * The currently selected funding source.
 * @returns {object|null}
 */
const selectedFundingSource = computed(() => {
  if (!props.fundingSources) return null
  const fundingSource = props.fundingSources.find(fundingSource => {
    return fundingSource.fundingSourceId === props.selectedFundingSourceId
  })
  return fundingSource !== undefined ? fundingSource : null
})

/**
 * Whether the currently selected funding source is using vericheck to
 * process payments
 * @returns {boolean}
 */
const fundingSourceIsVericheck = computed(() => {
  if (!selectedFundingSource.value) {
    return false
  }
  return selectedFundingSource.value.type === 'bank_account'
})

const requiresFeeCheckbox = computed(() => {
  if (props.orderEstimate) {
    for (let order of props.orderEstimate) {
      let hasProcessingFee =
        order.filter(item => item.orderItemType === OrderItemType.ProcessingFee).length > 0

      if (hasProcessingFee) {
        return true
      }
    }
  }
  return false
})

/**
 * The sum total of all the items in the order estimate
 * @returns {string}
 */
const orderEstimateTotal = computed(() => {
  let amount = 0
  if (props.orderEstimate === null) {
    return formatCurrency(amount)
  }
  props.orderEstimate.forEach(order => {
    order.forEach(item => {
      amount += item.amount
    })
  })
  return formatCurrency(amount)
})

onMounted(() => {
  eventBus.on('CHECKOUT_COMPLETED', (payload: unknown) => {
    const data = payload as { districtId?: number }
    if (data?.districtId === props.district.districtId) {
      emit('closeCheckoutModal')
    }
  })
})
</script>

<template>
  <Dialog
    :visible="modelValue"
    modal
    :dismissableMask="false"
    @update:visible="$emit('update:modelValue', $event)"
    @hide="$emit('closeCheckoutModal')"
    :baseZIndex="99999"
    header="Checkout"
  >
    <div :style="isMdOrGreater ? { width: '500px' } : { maxWidth: '500px' }">
      <div class="mb-5" v-if="orderEstimateTotal !== '$0.00'">
        <FundingSourceSelector
          :funding-sources="fundingSources"
          :selected-funding-source-id="selectedFundingSourceId"
          :district="district"
          @select-funding-source="$emit('select-funding-source', $event)"
        />
      </div>
      <div class="font-bold mb-5">{{ district.districtName }} ({{ district.districtId }})</div>
      <div v-if="loadingOrderEstimate && !hasOrderEstimate">
        <div class="text-center font-bold" style="min-height: 75px">
          <ProgressSpinner
            size="small"
            stroke-width="5"
            style="width: 50px; height: 50px"
            aria-label="Loading"
          />
        </div>
      </div>
      <div v-else :aria-busy="loadingOrderEstimate" :class="{ 'opacity-60': loadingOrderEstimate }">
        <order
          v-for="(order, index) in orderEstimate"
          :finalize-checkout="true"
          :order-number="Number(index) + 1"
          :order="order.flat()"
          :key="`order-${index}`"
        />
        <Divider />
        <div class="grid mb-3 font-bold">
          <div class="col-9">Grand Total:</div>
          <div class="col-3">{{ orderEstimateTotal }}</div>
        </div>
        <div v-if="requiresFeeCheckbox">
          <Checkbox v-model="feeConfirmation" inputId="fee-confirmation" binary />
          <label for="fee-confirmation" class="ml-2">
            By checking this box, I acknowledge the total to be charged to my
            <span v-if="!fundingSourceIsVericheck">credit card</span
            ><span v-else>bank account</span> includes the processing fee(s) itemized above.
          </label>
        </div>
        <div class="grid mb-3" v-if="autoPayWarning">
          <div class="col-12 text-sm text-secondary">
            <span class="font-bold text-sm text-red-500">Warning:</span>
            You are paying for a contract that has auto payment enabled. If you are behind on your
            payments and you make a manual payment, you will still be charged on your next auto
            payment date for this contract
          </div>
        </div>
        <div class="grid mb-2 mt-2">
          <div class="col-12 text-sm text-secondary">
            <span class="font-bold text-sm text-secondary">Notice:</span>
            Each order will appear as a separate charge in your statement.
          </div>
        </div>

        <div v-if="fundingSourceIsVericheck">
          <hr />
          <div>
            <p class="text-sm text-secondary">
              By entering your account information and clicking the button to submit a transaction,
              you authorize us to debit the bank account whose information you provided above for a
              one-time payment. The amount of the payment will equal the exact amount that is listed
              on the order total. After submitting this form you will have the ability to print or
              save a receipt to your computer. In the case of an ACH Transaction being rejected for
              Non Sufficient Funds (NSF), you understand that the business may at its discretion
              attempt to process the charge again within 30 days, and that you may be charged a
              return item fee of no more than $35.00.
            </p>
            <div>
              <Checkbox
                v-model="vericheckTermsAccepted"
                inputId="vericheck-terms-accepted"
                binary
              />
              <label for="vericheck-terms-accepted" class="ml-2"> I agree to these terms. </label>
            </div>
          </div>
        </div>

        <div class="flex justify-center mt-4 flex-wrap gap-3">
          <Button @click="$emit('checkout')" class="w-13rem" :disabled="payIsDisabled">
            {{ orderEstimateTotal === '$0.00' ? 'Complete Order' : $t('c.cart.payNow') }}
          </Button>
          <Button
            :label="$t('c.fundingForms.transitDialog.cancelLabel')"
            @click="$emit('closeCheckoutModal')"
            text
            raised
            class="w-13rem"
          />
        </div>
      </div>
    </div>
  </Dialog>
</template>

<style scoped></style>
